// Bluetooth Printer Service using Web Bluetooth API
// Supports ESC/POS thermal printers

class BluetoothPrinterService {
  constructor() {
    this.device = null;
    this.server = null;
    this.characteristic = null;
    this.isConnected = false;
    
    // Common Bluetooth printer service UUIDs
    this.PRINTER_SERVICE_UUIDS = [
      '000018f0-0000-1000-8000-00805f9b34fb', // Generic printer
      '0000ff00-0000-1000-8000-00805f9b34fb', // Common thermal printer
      '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Serial Port Profile
      'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // Custom printer service
    ];
    
    this.WRITE_CHARACTERISTIC_UUIDS = [
      '0000ff02-0000-1000-8000-00805f9b34fb',
      '00002af1-0000-1000-8000-00805f9b34fb',
      '49535343-8841-43f4-a8d4-ecbe34729bb3',
      '0000ffe1-0000-1000-8000-00805f9b34fb',
    ];
  }

  // Check if Web Bluetooth is supported
  isSupported() {
    return 'bluetooth' in navigator;
  }

  // Request and connect to a Bluetooth printer
  async connect() {
    if (!this.isSupported()) {
      throw new Error('Web Bluetooth no está soportado en este navegador. Usa Chrome o Edge.');
    }

    try {
      // Request device with printer services
      this.device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: this.PRINTER_SERVICE_UUIDS,
      });

      console.log('Device selected:', this.device.name);

      // Connect to GATT server
      this.server = await this.device.gatt.connect();
      console.log('Connected to GATT server');

      // Find printer service and characteristic
      await this.findPrinterCharacteristic();
      
      this.isConnected = true;
      
      // Listen for disconnection
      this.device.addEventListener('gattserverdisconnected', () => {
        this.isConnected = false;
        console.log('Printer disconnected');
      });

      return {
        name: this.device.name || 'Impresora Bluetooth',
        id: this.device.id,
      };
    } catch (error) {
      console.error('Connection error:', error);
      throw new Error(`Error al conectar: ${error.message}`);
    }
  }

  // Find the write characteristic for the printer
  async findPrinterCharacteristic() {
    const services = await this.server.getPrimaryServices();
    
    for (const service of services) {
      console.log('Found service:', service.uuid);
      
      try {
        const characteristics = await service.getCharacteristics();
        
        for (const char of characteristics) {
          console.log('  Characteristic:', char.uuid, 'Properties:', char.properties);
          
          // Check if this characteristic supports writing
          if (char.properties.write || char.properties.writeWithoutResponse) {
            this.characteristic = char;
            console.log('Using characteristic:', char.uuid);
            return;
          }
        }
      } catch (e) {
        console.log('Could not get characteristics for service:', service.uuid);
      }
    }

    throw new Error('No se encontró una característica de escritura en la impresora');
  }

  // Disconnect from printer
  disconnect() {
    if (this.device && this.device.gatt.connected) {
      this.device.gatt.disconnect();
    }
    this.isConnected = false;
    this.device = null;
    this.server = null;
    this.characteristic = null;
  }

  // Send raw data to printer
  async sendData(data) {
    if (!this.characteristic) {
      throw new Error('No hay impresora conectada');
    }

    // Split data into chunks (BLE has packet size limits, usually 20-512 bytes)
    const chunkSize = 100;
    const chunks = [];
    
    for (let i = 0; i < data.length; i += chunkSize) {
      chunks.push(data.slice(i, i + chunkSize));
    }

    for (const chunk of chunks) {
      if (this.characteristic.properties.writeWithoutResponse) {
        await this.characteristic.writeValueWithoutResponse(chunk);
      } else {
        await this.characteristic.writeValue(chunk);
      }
      // Small delay between chunks
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  }

  // Print a receipt
  async printReceipt(receiptData) {
    const encoder = new ESCPOSEncoder();
    const data = encoder.encodeReceipt(receiptData);
    await this.sendData(data);
  }
}

// ESC/POS Command Encoder
// Optimizado para papel de 58mm con 48mm de ancho de impresion (32 caracteres)
class ESCPOSEncoder {
  constructor() {
    // ESC/POS Commands
    this.ESC = 0x1B;
    this.GS = 0x1D;
    this.LF = 0x0A;
    
    // Ancho de impresion: 48mm = ~32 caracteres en fuente normal
    // En doble ancho: ~16 caracteres
    this.LINE_WIDTH = 32;
    this.DOUBLE_WIDTH_CHARS = 16;
    
    this.commands = {
      INIT: [this.ESC, 0x40], // Initialize printer
      ALIGN_LEFT: [this.ESC, 0x61, 0x00],
      ALIGN_CENTER: [this.ESC, 0x61, 0x01],
      ALIGN_RIGHT: [this.ESC, 0x61, 0x02],
      BOLD_ON: [this.ESC, 0x45, 0x01],
      BOLD_OFF: [this.ESC, 0x45, 0x00],
      DOUBLE_HEIGHT_ON: [this.GS, 0x21, 0x10],
      DOUBLE_WIDTH_ON: [this.GS, 0x21, 0x20],
      DOUBLE_SIZE_ON: [this.GS, 0x21, 0x30],
      NORMAL_SIZE: [this.GS, 0x21, 0x00],
      UNDERLINE_ON: [this.ESC, 0x2D, 0x01],
      UNDERLINE_OFF: [this.ESC, 0x2D, 0x00],
      CUT_PAPER: [this.GS, 0x56, 0x00], // Full cut
      PARTIAL_CUT: [this.GS, 0x56, 0x01], // Partial cut
      FEED_LINES: (n) => [this.ESC, 0x64, n], // Feed n lines
    };
  }

  // Remover acentos y caracteres especiales
  removeAccents(text) {
    const accentsMap = {
      'á': 'a', 'é': 'e', 'í': 'i', 'ó': 'o', 'ú': 'u',
      'Á': 'A', 'É': 'E', 'Í': 'I', 'Ó': 'O', 'Ú': 'U',
      'ñ': 'n', 'Ñ': 'N', 'ü': 'u', 'Ü': 'U',
      '¿': '?', '¡': '!', '°': 'o',
    };
    
    return text.split('').map(char => accentsMap[char] || char).join('');
  }

  // Convert string to bytes (ASCII simple)
  textToBytes(text) {
    const cleanText = this.removeAccents(text);
    const bytes = [];
    for (let i = 0; i < cleanText.length; i++) {
      let charCode = cleanText.charCodeAt(i);
      // Solo caracteres ASCII imprimibles
      if (charCode > 127) {
        charCode = 0x3F; // '?' para caracteres no soportados
      }
      bytes.push(charCode);
    }
    return bytes;
  }

  // Crear linea con padding (alineacion izquierda-derecha)
  formatLine(left, right, width = 32) {
    const cleanLeft = this.removeAccents(left);
    const cleanRight = this.removeAccents(right);
    const spaces = width - cleanLeft.length - cleanRight.length;
    return cleanLeft + ' '.repeat(Math.max(1, spaces)) + cleanRight;
  }

  // Centrar texto
  centerText(text, width = 32) {
    const cleanText = this.removeAccents(text);
    if (cleanText.length >= width) return cleanText.substring(0, width);
    const padding = Math.floor((width - cleanText.length) / 2);
    return ' '.repeat(padding) + cleanText;
  }

  // Linea separadora
  separator(char = '-', width = 32) {
    return char.repeat(width);
  }

  // Encode a full receipt
  encodeReceipt(data) {
    const bytes = [];
    const W = this.LINE_WIDTH; // 32 caracteres
    
    // Initialize printer
    bytes.push(...this.commands.INIT);
    
    // ========== HEADER ==========
    bytes.push(...this.commands.ALIGN_CENTER);
    bytes.push(...this.commands.BOLD_ON);
    
    // Nombre del negocio - en doble altura para que se vea grande pero quepa
    bytes.push(...this.commands.DOUBLE_HEIGHT_ON);
    const businessName = this.removeAccents(data.businessName || 'THE HOLLAND BARBERSHOP');
    // Si es muy largo, dividir en dos lineas
    if (businessName.length > W) {
      const words = businessName.split(' ');
      let line1 = '';
      let line2 = '';
      for (const word of words) {
        if ((line1 + ' ' + word).trim().length <= W) {
          line1 = (line1 + ' ' + word).trim();
        } else {
          line2 = (line2 + ' ' + word).trim();
        }
      }
      bytes.push(...this.textToBytes(line1));
      bytes.push(this.LF);
      if (line2) {
        bytes.push(...this.textToBytes(line2));
        bytes.push(this.LF);
      }
    } else {
      bytes.push(...this.textToBytes(businessName));
      bytes.push(this.LF);
    }
    bytes.push(...this.commands.NORMAL_SIZE);
    bytes.push(...this.commands.BOLD_OFF);
    
    // Sucursal
    if (data.branchName) {
      bytes.push(...this.textToBytes(this.removeAccents(data.branchName)));
      bytes.push(this.LF);
    }
    
    bytes.push(...this.textToBytes(this.separator()));
    bytes.push(this.LF);
    
    // ========== INFO DE VENTA ==========
    bytes.push(...this.commands.ALIGN_LEFT);
    
    // Fecha y hora en una linea
    const now = new Date();
    const dateStr = now.toLocaleDateString('es-MX', { 
      day: '2-digit', month: '2-digit', year: 'numeric' 
    });
    const timeStr = now.toLocaleTimeString('es-MX', { 
      hour: '2-digit', minute: '2-digit' 
    });
    bytes.push(...this.textToBytes(`Fecha: ${dateStr}  ${timeStr}`));
    bytes.push(this.LF);
    
    // Ticket number
    if (data.ticketNumber) {
      bytes.push(...this.textToBytes(`Ticket: #${data.ticketNumber}`));
      bytes.push(this.LF);
    }
    
    // Barber name
    if (data.barberName) {
      bytes.push(...this.textToBytes(`Atendio: ${this.removeAccents(data.barberName)}`));
      bytes.push(this.LF);
    }
    
    bytes.push(...this.textToBytes(this.separator()));
    bytes.push(this.LF);
    
    // ========== ITEMS ==========
    bytes.push(...this.commands.BOLD_ON);
    bytes.push(...this.textToBytes(this.formatLine('DESCRIPCION', 'IMPORTE', W)));
    bytes.push(this.LF);
    bytes.push(...this.commands.BOLD_OFF);
    
    for (const item of data.items || []) {
      const qty = item.quantity;
      const name = this.removeAccents(item.name);
      const total = (item.price * item.quantity).toFixed(2);
      
      // Formato: 1x Nombre          $XXX.XX
      const qtyStr = `${qty}x `;
      const priceStr = `$${total}`;
      const maxNameLen = W - qtyStr.length - priceStr.length - 1;
      const truncatedName = name.length > maxNameLen ? name.substring(0, maxNameLen) : name;
      
      bytes.push(...this.textToBytes(this.formatLine(qtyStr + truncatedName, priceStr, W)));
      bytes.push(this.LF);
    }
    
    bytes.push(...this.textToBytes(this.separator()));
    bytes.push(this.LF);
    
    // ========== TOTAL ==========
    bytes.push(...this.commands.BOLD_ON);
    bytes.push(...this.commands.DOUBLE_HEIGHT_ON);
    bytes.push(...this.textToBytes(this.formatLine('TOTAL:', `$${data.total.toFixed(2)}`, W)));
    bytes.push(this.LF);
    bytes.push(...this.commands.NORMAL_SIZE);
    bytes.push(...this.commands.BOLD_OFF);
    
    // Metodo de pago
    const paymentLabels = {
      'cash': 'Efectivo',
      'card': 'Tarjeta',
      'transfer': 'Transferencia',
      'combined': 'Combinado',
    };
    bytes.push(...this.textToBytes(`Pago: ${paymentLabels[data.paymentMethod] || data.paymentMethod}`));
    bytes.push(this.LF);
    
    // Combined payment details
    if (data.paymentMethod === 'combined' && data.cashAmount !== undefined) {
      bytes.push(...this.textToBytes(`  Efectivo: $${data.cashAmount.toFixed(2)}`));
      bytes.push(this.LF);
      bytes.push(...this.textToBytes(`  Tarjeta:  $${data.cardAmount.toFixed(2)}`));
      bytes.push(this.LF);
    }
    
    bytes.push(...this.textToBytes(this.separator()));
    bytes.push(this.LF);
    
    // ========== FOOTER ==========
    bytes.push(...this.commands.ALIGN_CENTER);
    
    // Mensaje de agradecimiento - completo y centrado
    const thankYou = 'Gracias por su preferencia!';
    bytes.push(...this.textToBytes(thankYou));
    bytes.push(this.LF);
    
    // Puntos de lealtad
    if (data.loyaltyPoints) {
      bytes.push(...this.textToBytes(`+${data.loyaltyPoints} puntos de lealtad`));
      bytes.push(this.LF);
    }
    
    // Feed y corte
    bytes.push(...this.commands.FEED_LINES(4));
    bytes.push(...this.commands.PARTIAL_CUT);
    
    return new Uint8Array(bytes);
  }
}

// Singleton instance
const bluetoothPrinter = new BluetoothPrinterService();

export { bluetoothPrinter, BluetoothPrinterService, ESCPOSEncoder };
